import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Container,
  useTheme,
  keyframes,
  styled,
  IconButton
} from '@mui/material';
import {
  FlashOnRounded,
  TrendingUpRounded,
  Nature,
  SecurityRounded,
  Speed,
  Assessment,
  EnergySavingsLeafRounded,
  BatteryChargingFullRounded
} from '@mui/icons-material';

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled Components
const InteractiveCard = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: '24px',
  borderRadius: '20px',
  background: `linear-gradient(135deg, ${theme.palette.background.paper}90 0%, ${theme.palette.background.paper}75 40%, ${theme.palette.primary.main}08 75%, ${theme.palette.primary.main}05 100%)`,
  backdropFilter: 'blur(15px)',
  border: `1px solid ${theme.palette.primary.main}20`,
  boxShadow: `0 8px 32px ${theme.palette.primary.main}10`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}15, transparent)`,
    transition: 'left 0.6s',
    zIndex: 1,
  },

  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: `0 20px 60px ${theme.palette.primary.main}25`,
    border: `1px solid ${theme.palette.primary.main}40`,
    background: `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.background.paper}85 35%, ${theme.palette.primary.main}12 70%, ${theme.palette.primary.main}10 100%)`,

    '&::before': {
      left: '100%',
    },

    '& .feature-icon': {
      color: theme.palette.primary.main,
      transform: 'scale(1.2) rotate(10deg)',
      background: `${theme.palette.primary.main}15`,
      boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
    },

    '& .feature-stats': {
      opacity: 1,
      transform: 'translateY(0)',
    },

    '& .feature-glow': {
      opacity: 1,
      animation: `${pulse} 2s infinite`,
    }
  }
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${theme.palette.primary.main}10`,
  border: `2px solid ${theme.palette.primary.main}20`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  marginBottom: '20px',
  position: 'relative',
  zIndex: 2,

  '& svg': {
    fontSize: '40px',
    color: theme.palette.text.secondary,
    transition: 'all 0.3s ease',
  }
}));

const FeatureStats = styled(Box)(({ theme }) => ({
  opacity: 0,
  transform: 'translateY(10px)',
  transition: 'all 0.3s ease',
  marginTop: '16px',
  padding: '12px 16px',
  borderRadius: '12px',
  background: `${theme.palette.primary.main}08`,
  border: `1px solid ${theme.palette.primary.main}15`,
  backdropFilter: 'blur(10px)',
}));

const GlowEffect = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-50%',
  left: '-50%',
  width: '200%',
  height: '200%',
  background: `radial-gradient(circle, ${theme.palette.primary.main}20 0%, transparent 70%)`,
  opacity: 0,
  transition: 'opacity 0.3s ease',
  zIndex: 0,
}));

interface FeatureData {
  icon: React.ReactElement;
  title: string;
  description: string;
  stats: {
    label: string;
    value: string;
    color?: 'primary' | 'success' | 'info' | 'warning';
  };
  delay: number;
}

const features: FeatureData[] = [
  {
    icon: <FlashOnRounded />,
    title: 'Schnelle Analyse',
    description: 'Automatisierte Bewertung Ihres Leistungsprofils basierend auf etablierten Algorithmen',
    stats: { label: 'Geschätzte Analysezeit', value: '1-2 Min', color: 'success' },
    delay: 100
  },
  {
    icon: <TrendingUpRounded />,
    title: 'Datenbasierte Bewertung',
    description: 'Systematische Empfehlungen für Energiespeicher-Lösungen basierend auf Ihrem Profil',
    stats: { label: 'Beta-Version', value: 'V1.0', color: 'primary' },
    delay: 200
  },
  {
    icon: <Nature />,
    title: 'Nachhaltige Technologie',
    description: 'Potentiale für CO₂-Reduktion und Kosteneinsparungen durch optimierte Systeme identifizieren',
    stats: { label: 'Potential-Analyse', value: 'Ja', color: 'success' },
    delay: 300
  },
  {
    icon: <SecurityRounded />,
    title: 'Sichere Datenverarbeitung',
    description: 'DSGVO-konforme Analyse mit höchsten Sicherheitsstandards und Ende-zu-Ende Verschlüsselung',
    stats: { label: 'Sicherheits-Zertifizierung', value: 'ISO 27001', color: 'info' },
    delay: 400
  }
];

const InteractiveFeatureCards: React.FC = () => {
  const theme = useTheme();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <Container maxWidth={false} sx={{
      py: 8,
      px: { xs: 2, sm: 4, md: 6 },
      maxWidth: '1400px',
      mx: 'auto'
    }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            mb: 2,
          }}
        >
          Innovative Features
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Erleben Sie die Zukunft der Energiespeicher-Analyse mit unseren hochmodernen KI-Technologien
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Box
              sx={{
                animation: `${float} 6s ease-in-out infinite`,
                animationDelay: `${feature.delay}ms`,
              }}
            >
              <InteractiveCard
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <GlowEffect className="feature-glow" />

                <FeatureIcon className="feature-icon">
                  {feature.icon}
                </FeatureIcon>

                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  {feature.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.6,
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  {feature.description}
                </Typography>

                <FeatureStats className="feature-stats">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {feature.stats.label}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: `${feature.stats.color || 'primary'}.main`,
                        fontWeight: 600,
                      }}
                    >
                      {feature.stats.value}
                    </Typography>
                  </Box>
                </FeatureStats>
              </InteractiveCard>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Floating Background Elements */}
      {hoveredCard !== null && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 50% 50%, ${theme.palette.primary.main}03 0%, transparent 50%)`,
            animation: `${pulse} 3s infinite`,
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
      )}
    </Container>
  );
};

export default InteractiveFeatureCards;